import React from 'react';
import LegalLayout from './LegalLayout';

const Terms = () => {
  return (
    <LegalLayout
      testId="legal-terms-page"
      title="Terms & Conditions"
      lastUpdated="February 2026"
      intro="These Terms govern your access to and use of the Daye Farm Ltd website, Farmers Market e-commerce, Wholesale platform, Investment portal and related services. By using our services, you agree to be bound by these Terms."
    >
      <h2>1. About Daye Farm Ltd</h2>
      <p>
        Daye Farm Ltd ("Daye Farm", "we", "our" or "us") is an integrated agricultural enterprise
        registered in the Federal Republic of Nigeria with operations in livestock, crop farming,
        agro-processing and distribution. Our registered address is 110 Ankpa Ayingba Road, Kogi,
        Nigeria. References to "you" or "User" mean any person accessing or using our services.
      </p>

      <h2>2. Acceptance of Terms</h2>
      <p>
        By creating an account, placing an order, submitting an investment expression of interest or
        otherwise using any feature of this site, you confirm that you have read, understood and
        accepted these Terms, our <a href="/legal/privacy">Privacy Policy</a> and our{' '}
        <a href="/legal/cookies">Cookie Notice</a>. If you do not agree, you must not use the
        services.
      </p>

      <h2>3. Eligibility</h2>
      <ul>
        <li>You must be at least 18 years old and able to enter into a legally binding contract.</li>
        <li>You must provide accurate, complete and current information when registering.</li>
        <li>
          Investor and wholesale account holders may be required to provide additional verification
          documents for KYC and AML compliance.
        </li>
      </ul>

      <h2>4. Accounts &amp; Security</h2>
      <p>
        You are responsible for safeguarding your login credentials and for any activity carried out
        under your account. We use secure <code>httpOnly</code> session cookies for authentication.
        You agree to notify us immediately at{' '}
        <a href="mailto:privacy@dayefarmltd.com">privacy@dayefarmltd.com</a> if you suspect any
        unauthorised access.
      </p>

      <h2>5. Products, Pricing &amp; Orders</h2>
      <ul>
        <li>
          All product descriptions, weights, photographs and prices are provided in good faith and
          may change without prior notice. We reserve the right to correct typographical errors.
        </li>
        <li>
          Placing an order is an offer to purchase; a binding contract is formed only when we issue
          an order confirmation.
        </li>
        <li>
          We may cancel or refuse any order at our discretion, including suspected fraud, pricing
          errors, stock shortages or breach of these Terms.
        </li>
      </ul>

      <h2>6. Payments</h2>
      <p>
        Payments are processed by reputable third-party providers (e.g. Paystack). We do not store
        full card details on our servers. You authorise the relevant provider to charge the
        applicable amount, taxes and delivery fees to your chosen payment method.
      </p>

      <h2>7. Delivery, Returns &amp; Refunds</h2>
      <ul>
        <li>
          Delivery timelines depend on location, product type and logistics partner. Perishable
          farm produce may require cold-chain handling and is dispatched within agreed windows.
        </li>
        <li>
          Returns are accepted only where products are received damaged, spoiled or materially
          different from the order. Notice must be given within 24 hours of delivery for
          perishables and 7 days for non-perishables.
        </li>
        <li>
          Approved refunds are issued through the original payment channel within 7–14 business
          days.
        </li>
      </ul>

      <h2>8. Wholesale &amp; B2B Use</h2>
      <p>
        Wholesale customers must comply with any additional commercial agreements, minimum order
        quantities and credit terms communicated by our wholesale desk. Resale of our products under
        any branding other than agreed channels is prohibited.
      </p>

      <h2>9. Investment Services</h2>
      <p>
        Information on our investment portal is provided for general informational purposes only and
        does not constitute financial, legal or tax advice. Agricultural investments carry risk,
        including but not limited to weather, market and biological risk. You should seek
        independent advice before committing funds. Past performance is not indicative of future
        results.
      </p>

      <h2>10. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the services for any unlawful, fraudulent or harmful purpose.</li>
        <li>
          Attempt to gain unauthorised access to our systems, accounts, networks or any part of the
          site.
        </li>
        <li>
          Upload viruses, scrape content, reverse-engineer our software or interfere with the proper
          working of the platform.
        </li>
        <li>Impersonate any person or misrepresent your affiliation with any person or entity.</li>
      </ul>

      <h2>11. Intellectual Property</h2>
      <p>
        All content on this website — including text, graphics, logos, photographs, the Daye Farm
        brand and the underlying software — is the property of Daye Farm Ltd or its licensors and is
        protected by Nigerian and international intellectual property laws. You are granted a
        limited, non-exclusive, revocable licence to use the services for their intended purpose.
      </p>

      <h2>12. User-Generated Content</h2>
      <p>
        Any feedback, reviews or content you submit must be lawful, accurate and not infringe any
        third-party rights. By submitting content you grant us a worldwide, royalty-free licence to
        use, reproduce and display it in connection with our services.
      </p>

      <h2>13. Third-Party Services</h2>
      <p>
        Our services may integrate with third-party providers such as payment processors, analytics
        tools, social media platforms and authentication services. We are not responsible for the
        practices of those third parties and recommend you review their respective terms.
      </p>

      <h2>14. Disclaimers</h2>
      <p>
        The services are provided on an "as is" and "as available" basis. To the maximum extent
        permitted by law, Daye Farm disclaims all warranties, express or implied, including
        merchantability, fitness for a particular purpose and non-infringement.
      </p>

      <h2>15. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Daye Farm and its directors, officers, employees
        and agents shall not be liable for any indirect, incidental, special, consequential or
        punitive damages arising from your use of the services. Our aggregate liability for any
        claim shall not exceed the amount paid by you to Daye Farm in the twelve (12) months
        preceding the event giving rise to the claim.
      </p>

      <h2>16. Indemnity</h2>
      <p>
        You agree to indemnify and hold Daye Farm harmless from any loss, claim or expense arising
        out of your breach of these Terms or your misuse of the services.
      </p>

      <h2>17. Suspension &amp; Termination</h2>
      <p>
        We may suspend or terminate your access at any time for breach of these Terms, suspected
        fraud or where required by law. Provisions intended to survive termination (including
        intellectual property, indemnity and limitation of liability) shall survive.
      </p>

      <h2>18. Governing Law &amp; Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of the Federal Republic of Nigeria. Any dispute arising
        out of or in connection with these Terms shall be subject to the exclusive jurisdiction of
        the competent courts in Nigeria, without prejudice to any mandatory consumer rights you may
        have under local law.
      </p>

      <h2>19. Changes to these Terms</h2>
      <p>
        We may revise these Terms from time to time. Material changes will be communicated through
        the site or by email where appropriate. Continued use after a revision constitutes
        acceptance of the updated Terms.
      </p>

      <h2>20. Contact</h2>
      <p>
        Questions about these Terms can be sent to{' '}
        <a href="mailto:privacy@dayefarmltd.com">privacy@dayefarmltd.com</a> or by post to Daye
        Farm Ltd, 110 Ankpa Ayingba Road, Kogi, Nigeria.
      </p>
    </LegalLayout>
  );
};

export default Terms;
